import { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import toast from 'react-hot-toast';
import { store, useAppSelector } from '../../stores/AppStore';
import {
  selectMentorUserId,
  selectPaymentIntentId,
  selectThinkers,
  resetSignUp,
} from '../../features/signUp/signUpSlice';
import { selectIsAuthenticated } from '../../features/user/userSlice';
import { useGetMentorPasswordResetLinkByPaymentIdQuery } from '../../services/apiSlice';

const SubscriptionConfirm = () => {
  const navigate = useNavigate();
  const paymentIntentId = useAppSelector(selectPaymentIntentId);
  const mentorUserId = useAppSelector(selectMentorUserId);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const thinkers = useAppSelector(selectThinkers);

  // Get password reset link for mentor (user was already created during payment)
  const {
    data: passwordResetData,
    isLoading,
    isError,
  } = useGetMentorPasswordResetLinkByPaymentIdQuery(
    {
      paymentIntentId: paymentIntentId ?? '',
      mentorUserId: mentorUserId ?? '',
    },
    {
      skip: !paymentIntentId || !mentorUserId,
    },
  );

  // Extract password reset link and hasPassword flag from response
  const passwordResetLink = passwordResetData?.passwordResetLink;
  const hasExistingPassword = passwordResetData?.hasPassword;

  // Determine if this is a returning customer (existing mentor with password)
  const isReturningCustomer = hasExistingPassword === true;

  // Determine if we can't proceed (no data available at all)
  const noDataAvailable =
    !isLoading &&
    (isError || !passwordResetData || !paymentIntentId || !mentorUserId);

  // Auto-clear signup state and redirect based on auth status
  useEffect(() => {
    if (isAuthenticated) {
      // User is already logged in - redirect to dashboard after showing success page
      const timer = setTimeout(() => {
        Sentry.addBreadcrumb({
          category: 'signup',
          message:
            'Auto-clearing signup state - authenticated user redirecting to dashboard',
          level: 'info',
          data: {
            isAuthenticated: true,
            redirectTo: '/mentor/thinkers',
          },
        });
        localStorage.removeItem('signUp-state');
        store.dispatch(resetSignUp(0));
        navigate('/mentor/thinkers');
      }, 7000); // Redirect after 7 seconds

      return () => clearTimeout(timer);
    } else if (noDataAvailable) {
      // No data available (error or missing params) - redirect to login
      toast.success(
        'Your signup is complete! Please log in to access your dashboard.',
        { id: 'signup-complete', duration: 7000 },
      );
      const timer = setTimeout(() => {
        Sentry.addBreadcrumb({
          category: 'signup',
          message:
            'Auto-clearing signup state - redirecting to login (no data available)',
          level: 'info',
          data: {
            isAuthenticated: false,
            noDataAvailable: true,
            redirectTo: '/login',
          },
        });
        localStorage.removeItem('signUp-state');
        store.dispatch(resetSignUp(0));
        navigate('/login');
      }, 7000); // Redirect after 7 seconds

      return () => clearTimeout(timer);
    } else if (passwordResetLink || isReturningCustomer) {
      // User not logged in - clear localStorage but stay on this page
      // Don't reset step - user should stay on success page until they click the button
      const timer = setTimeout(() => {
        Sentry.addBreadcrumb({
          category: 'signup',
          message:
            'Clearing localStorage after successful subscription completion',
          level: 'info',
          data: {
            isAuthenticated: false,
            hasPasswordResetLink: !!passwordResetLink,
            isReturningCustomer,
          },
        });
        localStorage.removeItem('signUp-state');
        // Note: Not dispatching resetSignUp() here - user stays on success page
      }, 2000); // Clear localStorage after 2 seconds

      return () => clearTimeout(timer);
    }
  }, [
    isAuthenticated,
    noDataAvailable,
    passwordResetLink,
    isReturningCustomer,
    navigate,
  ]);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-8 justify-center items-center h-full w-full">
        <img src="/images/loading.svg" alt="Loading" />
        <p className="mb-4 text-center font-medium">
          Preparing your login link...
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col md:flex-row gap-2 md:gap-6">
      <div
        data-testid="image-grid"
        className="grid grid-rows-2 grid-cols-1 gap-2 md:gap-4 md:w-1/2 order-2 md:order-1"
      >
        <div className="grid grid-cols-[55%_45%]">
          <div className="row-span-2 col-span-1 pr-2 md:pr-4">
            <img
              src="/images/matt.png"
              alt="Matt Miklius"
              className="w-full h-full rounded-xl object-cover"
            />
          </div>
          <div className="row-start-1 col-start-2 pb-2 md:pb-4">
            <img
              src="/images/window-desk.png"
              alt="Children at window Desk"
              className="w-full h-full rounded-xl object-cover"
            />
          </div>
          <div className="row-start-2 col-start-2">
            <img
              src="/images/garage-door.png"
              alt="Children front of garage door"
              className="w-full h-full rounded-xl object-cover"
            />
          </div>
        </div>
        <div className="grid grid-cols-[45%_55%]">
          <div className="row-start-1 col-start-1 pb-2 md:pb-4">
            <img
              src="/images/cap.png"
              alt="Cap"
              className="h-full w-full rounded-xl object-cover"
            />
          </div>
          <div className="row-start-2 col-start-1">
            <img
              src="/images/tutor.png"
              alt="Tutoring"
              className="h-full w-full rounded-xl object-cover"
            />
          </div>
          <div className="row-start-1 col-start-2 row-span-2 pl-2 md:pl-4">
            <img
              src="/images/office.png"
              alt="Office"
              className="h-full w-full rounded-xl object-cover"
            />
          </div>
        </div>
      </div>

      <div
        data-testid="welcome-message"
        className="flex flex-col gap-4 bg-gray-100 p-4 md:p-8 lg:p-12 rounded-xl md:w-1/2 order-1 md:order-2"
      >
        <img
          src="/images/logo-transparent.png"
          alt="The Think Tank Logo"
          className="hidden md:block md:w-[130px] lg:w-[160px] lg:pb-2"
        />
        <h1 className="text-2xl md:text-3xl font-medium">
          {isReturningCustomer ? (
            <>
              Thank You for Your <br />
              Continued Trust!
            </>
          ) : (
            <>
              Congratulations and Welcome <br />
              to Fill the Math GAPS Program!
            </>
          )}
        </h1>
        <p className="text-sm md:text-base text-gray-500">
          {isReturningCustomer
            ? "Thank you for adding another Thinker to your account! We're thrilled to continue supporting your child's math journey."
            : "Your subscription is complete! You've taken an important step in empowering your child to excel in math."}
        </p>
        <p className="text-sm md:text-base text-gray-500">
          {isReturningCustomer
            ? 'Your new Thinker has been added and is ready to start learning. Log in to access your dashboard and begin.'
            : "We're excited to have you on board and can't wait for your child to start exploring, and growing with us."}
        </p>
        <div className="rounded-lg border border-blue-200 bg-white p-4 text-sm">
          <p className="font-bold text-blue-900">
            Two accounts are used for the program
          </p>
          <ol className="mt-3 space-y-3 text-gray-700">
            <li>
              <span className="font-medium">1. Mentor account</span>
              <p>Use your account to monitor assignments and progress.</p>
            </li>
            <li>
              <span className="font-medium">2. Thinker account</span>
              <p>
                We sent a separate setup email to each Thinker. They must set a
                password and sign in with their own email to complete quizzes
                and save progress.
              </p>
              <ul className="mt-2 list-disc pl-5">
                {thinkers.map((thinker) => (
                  <li key={thinker.email}>
                    {thinker.firstName} {thinker.lastName}: {thinker.email}
                  </li>
                ))}
              </ul>
            </li>
          </ol>
          <p className="mt-3 font-medium text-blue-900">
            Quizzes taken while signed in as the Mentor are for practice or
            preview only.
          </p>
        </div>
        <img
          src="/images/Matt-signature.png"
          alt="Signature"
          className="h-[50px] md:h-[70px] w-max"
        />
        <p className="text-sm md:text-base text-gray-500">
          Matt Miklius, PhD, Founder
        </p>
        <div className="flex flex-col md:flex-row flex-wrap gap-4 pt-4 mt-auto md:mt-16 justify-between items-center">
          {isAuthenticated ? (
            <>
              <h1 className="text-lg md:text-xl font-medium">
                Your account is ready!
              </h1>
              <p className="text-sm text-gray-500">
                Redirecting you to your dashboard...
              </p>
            </>
          ) : noDataAvailable ? (
            <>
              <h1 className="text-lg md:text-xl font-medium">
                Your account is ready!
              </h1>
              <p className="text-sm text-gray-500">
                Redirecting you to login...
              </p>
            </>
          ) : isReturningCustomer ? (
            <>
              <h1 className="text-lg md:text-xl font-medium">Welcome Back!</h1>
              <Link
                data-testid="login-button"
                to="/login"
                className="
                  px-8 py-2 rounded-full w-full md:w-max text-center
                  font-medium text-white bg-tttDefault
                  transition-all duration-300 ease-in-out
                  hover:shadow hover:bg-opacity-90
                "
              >
                Log In to Your Account
              </Link>
            </>
          ) : (
            <>
              <h1 className="text-lg md:text-xl font-medium">
                Start Exploring the App Now
              </h1>
              <button
                data-testid="continue-button"
                disabled={!passwordResetLink}
                onClick={() => {
                  if (passwordResetLink) {
                    window.location.href = passwordResetLink;
                  }
                }}
                title={
                  passwordResetLink
                    ? 'Click to set your password and begin your journey'
                    : 'Please check your welcome email or contact us at help@thinktankprep.com if you have not received one'
                }
                className="
                  px-8 py-2 rounded-full w-full md:w-max
                  font-medium text-white bg-tttDefault
                  transition-all duration-300 ease-in-out
                  hover:shadow hover:bg-opacity-90
                "
              >
                Get Started
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionConfirm;
